import React, { useState } from 'react';
import {
  FaCalculator, FaMapMarkerAlt, FaBed, FaUtensils, FaBus, FaTicketAlt,
  FaLightbulb, FaSpinner, FaCalendarAlt, FaMagic,
} from 'react-icons/fa';
import { getTripCostEstimate, trackEvent } from '../../api/client';
import BookingBlock from '../BookingBlock';
import { setPendingTripContext } from '../../utils/tripContext';

/**
 * Trip Cost Estimator — "what will this trip cost?", answered by the AI for the
 * destination and length the visitor asks about, never from a stored price
 * table.
 *
 * It earns its place twice over: a budget is the most reliably engaging module
 * in travel content, and its accommodation line is the most natural place on
 * the whole site to offer a live hotel search. The estimate is a range, and the
 * prices next to it come from Booking.com itself — nothing here fabricates a
 * price for a real property.
 */

const CATEGORY_ICONS = {
  accommodation: FaBed,
  food: FaUtensils,
  local_transport: FaBus,
  attractions: FaTicketAlt,
};

const STYLES = [
  { id: 'budget', label: 'Budget' },
  { id: 'mid-range', label: 'Mid-range' },
  { id: 'luxury', label: 'Luxury' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY', 'SGD', 'AED'];

const money = (value, symbol, code) => {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  const formatted = rounded.toLocaleString(undefined, { maximumFractionDigits: rounded >= 100 ? 0 : 2 });
  return symbol ? `${symbol}${formatted}` : `${formatted} ${code}`;
};

const TripCostEstimator = ({
  initialDestination = '',
  // Embedded mode: used inside an article or the preview, where the page has
  // its own heading and the visitor arrived for something else.
  compact = false,
  surface = 'tool_trip-cost',
  onPlanTrip,
}) => {
  const [destination, setDestination] = useState(initialDestination);
  const [days, setDays] = useState(5);
  const [travelers, setTravelers] = useState(2);
  const [style, setStyle] = useState('mid-range');
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estimate, setEstimate] = useState(null);

  const handleEstimate = async (e) => {
    if (e) e.preventDefault();
    const dest = destination.trim();
    if (dest.length < 2) {
      setError('Enter the place you want to price up');
      return;
    }
    setError('');
    setLoading(true);
    setEstimate(null);
    trackEvent('trip_cost_requested', { destination: dest, days, travelers, style, surface });
    try {
      const data = await getTripCostEstimate({ destination: dest, days, travelers, style, currency });
      setEstimate(data);
      // The visitor has just told us where and for how long — that is a trip
      // brief, and it should follow them into the planner.
      setPendingTripContext({ destination: dest, days, source: surface });
      trackEvent('trip_cost_rendered', { destination: dest, cached: !!data.cached });
    } catch (err) {
      setError(
        err?.response?.status === 502
          ? 'Could not price this destination right now. Try again in a moment.'
          : 'Something went wrong estimating this trip.'
      );
    } finally {
      setLoading(false);
    }
  };

  const accommodation = estimate?.categories?.find((c) => c.key === 'accommodation');
  const others = estimate?.categories?.filter((c) => c.key !== 'accommodation') || [];

  const form = (
    <form onSubmit={handleEstimate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="sm:col-span-2">
        <span className="block text-xs font-medium text-neutral-500 mb-1">Destination</span>
        <div className="flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-lg focus-within:ring-2 focus-within:ring-primary-400">
          <FaMapMarkerAlt className="text-primary-500 flex-shrink-0" />
          <input
            type="text"
            value={destination}
            onChange={(ev) => setDestination(ev.target.value)}
            placeholder="e.g. Lisbon, Portugal"
            maxLength={120}
            className="flex-1 bg-transparent border-0 focus:outline-none text-sm min-w-0"
            aria-label="Destination"
          />
        </div>
      </label>

      <label>
        <span className="block text-xs font-medium text-neutral-500 mb-1">Days</span>
        <input
          type="number"
          min={1}
          max={60}
          value={days}
          onChange={(ev) => setDays(Math.max(1, Math.min(60, Number(ev.target.value) || 1)))}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </label>

      <label>
        <span className="block text-xs font-medium text-neutral-500 mb-1">Travellers</span>
        <input
          type="number"
          min={1}
          max={20}
          value={travelers}
          onChange={(ev) => setTravelers(Math.max(1, Math.min(20, Number(ev.target.value) || 1)))}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </label>

      <label>
        <span className="block text-xs font-medium text-neutral-500 mb-1">Travel style</span>
        <select
          value={style}
          onChange={(ev) => setStyle(ev.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          {STYLES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </label>

      <label>
        <span className="block text-xs font-medium text-neutral-500 mb-1">Currency</span>
        <select
          value={currency}
          onChange={(ev) => setCurrency(ev.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </label>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={loading}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl shadow-sm hover:shadow-md disabled:opacity-60 transition-all"
        >
          {loading ? <FaSpinner className="animate-spin" /> : <FaCalculator />}
          {loading ? 'Working it out…' : 'Estimate this trip'}
        </button>
      </div>
    </form>
  );

  const results = estimate && (
    <div className="space-y-5">
      {/* Headline range */}
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">
          {estimate.days} days in {estimate.destination} · {estimate.travelers} traveller
          {estimate.travelers === 1 ? '' : 's'} · {estimate.style}
        </p>
        <p className="text-3xl sm:text-4xl font-bold text-neutral-900">
          {money(estimate.total_low, estimate.currency_symbol, estimate.currency)}
          {' – '}
          {money(estimate.total_high, estimate.currency_symbol, estimate.currency)}
        </p>
        <p className="text-sm text-neutral-600 mt-1">
          Roughly {money(estimate.per_person_low, estimate.currency_symbol, estimate.currency)}–
          {money(estimate.per_person_high, estimate.currency_symbol, estimate.currency)} per person,
          excluding flights.
        </p>
        {estimate.summary && (
          <p className="text-sm text-neutral-700 leading-relaxed mt-3">{estimate.summary}</p>
        )}
      </div>

      {/* Accommodation first, with the live hotel search directly beneath it —
          the estimate says roughly what a bed costs, Booking.com says what this
          one actually costs tonight. */}
      {accommodation && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-blue-50 rounded-xl flex-shrink-0">
              <FaBed className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="font-semibold text-neutral-900">{accommodation.label}</h4>
                <p className="font-bold text-neutral-900">
                  {money(accommodation.low, estimate.currency_symbol, estimate.currency)}–
                  {money(accommodation.high, estimate.currency_symbol, estimate.currency)}
                  <span className="ml-1 text-xs font-normal text-neutral-500">{accommodation.basis}</span>
                </p>
              </div>
              {accommodation.notes && (
                <p className="text-sm text-neutral-600 leading-relaxed mt-1">{accommodation.notes}</p>
              )}
              <p className="text-xs text-neutral-500 mt-2">
                {estimate.nights} night{estimate.nights === 1 ? '' : 's'} ·{' '}
                {money(accommodation.low * estimate.nights, estimate.currency_symbol, estimate.currency)}–
                {money(accommodation.high * estimate.nights, estimate.currency_symbol, estimate.currency)} in total
              </p>
            </div>
          </div>

          <BookingBlock
            destination={estimate.destination}
            surface={`${surface}_hotels`}
            heading={`Check real hotel prices in ${estimate.destination}`}
            className="!my-4"
          />
        </div>
      )}

      {/* The rest of the budget */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {others.map((c) => {
          const Icon = CATEGORY_ICONS[c.key] || FaCalculator;
          return (
            <div key={c.key} className="rounded-2xl border border-neutral-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="text-neutral-400" />
                <h4 className="font-semibold text-neutral-900 text-sm">{c.label}</h4>
              </div>
              <p className="font-bold text-neutral-900">
                {money(c.low, estimate.currency_symbol, estimate.currency)}–
                {money(c.high, estimate.currency_symbol, estimate.currency)}
              </p>
              <p className="text-xs text-neutral-500">{c.basis}</p>
              {c.notes && <p className="text-sm text-neutral-600 leading-relaxed mt-2">{c.notes}</p>}
            </div>
          );
        })}
      </div>

      {estimate.cheapest_months && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <FaCalendarAlt className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-neutral-700">
            <span className="font-semibold">Cheapest time to go: </span>
            {estimate.cheapest_months}
          </p>
        </div>
      )}

      {estimate.tips?.length > 0 && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <h4 className="font-semibold text-neutral-900 flex items-center gap-2 mb-3">
            <FaLightbulb className="text-amber-500" /> Where to save in {estimate.destination}
          </h4>
          <ul className="space-y-2">
            {estimate.tips.map((tip, i) => (
              <li key={i} className="text-sm text-neutral-700 leading-relaxed flex gap-2">
                <span className="text-emerald-500 flex-shrink-0">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The estimate answers "can I afford it?"; this answers "what would it
          actually look like?" — and the destination and length are already known. */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-50 to-transparent border-l-4 border-primary-500 p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-[14rem] flex-1">
          <p className="font-semibold text-neutral-900 mb-0.5">
            Now see what those {estimate.days} days look like
          </p>
          <p className="text-sm text-neutral-600">
            A free AI day-plan for {estimate.destination}, with real places and maps.
          </p>
        </div>
        <button
          onClick={() => {
            trackEvent('trip_cost_plan_clicked', { destination: estimate.destination, surface });
            if (onPlanTrip) onPlanTrip(estimate.destination);
            else window.location.href = '/tryout';
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors whitespace-nowrap"
        >
          <FaMagic size={13} /> Plan {estimate.destination} free
        </button>
      </div>

      <p className="text-xs text-neutral-500">
        Figures are AI-generated ranges for planning, not quotes. Live prices come from the booking
        partners themselves.
      </p>
    </div>
  );

  if (compact) {
    return (
      <section className="my-10 rounded-xl border border-neutral-200 bg-white p-5 sm:p-6">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2">
          <FaCalculator className="text-neutral-400" /> Trip budget
        </div>
        <h3 className="font-serif text-xl font-bold text-neutral-900 mb-3 leading-snug">
          What will {initialDestination || 'this trip'} cost?
        </h3>
        {!estimate && form}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {estimate && (
          <>
            {results}
            <button
              onClick={() => setEstimate(null)}
              className="mt-4 text-sm font-medium text-neutral-500 hover:text-primary-600"
            >
              Change the trip
            </button>
          </>
        )}
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute -right-10 top-1/2 -translate-y-1/2">
          <div className="w-40 h-40 bg-white/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaCalculator className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">What Will This Trip Cost?</h2>
              <p className="text-white/70">Accommodation, food, transport and attractions</p>
            </div>
          </div>
          <p className="text-white/80 mt-4">
            Tell us where you're going and for how long. You get a realistic range for each part of
            the budget, generated for that destination — then check live hotel prices against it.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-emerald-500" /> Your trip
        </h3>
        {form}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      {results}
    </div>
  );
};

export default TripCostEstimator;
